import { Col, Row } from "react-grid-system";
import Container from "../common/Container";
import { toolTypeToName } from "../AccountDetails/utils";
import { ToolType } from "../AccountDetails";
import StyledButton from "../common/buttons/StyledButton";
import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./PaperlessService.module.css";
import classNames from "classnames";
import Spinner from "../common/Spinner";
import StyledCheckbox from "../common/inputs/StyledCheckbox";
import { useId, useEffect, useState, useRef } from "react";
import {
  useGetPaperlessSettings,
  useUpdatePaperlessSettings,
} from "../../lib/api/queries";
import {
  getPaperlessServiceScriptDetails,
  PaperlessServiceScriptDetails,
} from "../../lib/scripts/paperlessService/paperlessServiceScripts";
import ConversationScript from "../common/ConversationScript";
import ToolLoadError from "../common/ToolLoadError";
import { getDisplayError } from "../../lib/api/endpoints";
import { useChannelState } from "../../lib/channelContext";
import { ChannelMapToFull } from "../../lib/channelUtil";

const strings = {
  close: "Close",
  update: "Update",

  paperlessStatusTitle: "Paperless Service Status:",
  paperlessStatusLabel: "Stop Paperless Service:",
  emailStatusTitle: "Online Statement E-mail Status:",
  emailStatusLabel: "Do not send Online Statement E-mail:",
  emailContactTitle: "Email Contact:",
  emailContactDescription:
    "Changes to the primary and alternate email address(es), are to be completed in the CM INFO tool.",
  emailContactOptedOut: "User opted out of email contact.",
  primaryEmail: "Primary Email",
  alternateEmail: "Alternate Email",

  a0162: "A_0162 - You must enter a primary e-mail address.",
};

type Props = {
  className?: string;
  dwbuid: string;
  toolId: string;
  partyId: string;
  referenceId?: string;
  onRequestClose: () => void;
  onDisplayGeneralErrors: (error: unknown) => void;
  onSetToolInProgress: (id: string, inProgress: boolean) => void;
  isDelinquent: boolean;
  isDisabled: boolean;
};

export default function PaperlessService({
  className,
  dwbuid,
  toolId,
  partyId,
  referenceId,
  onRequestClose,
  onDisplayGeneralErrors,
  onSetToolInProgress,
  isDelinquent,
  isDisabled,
}: Props) {
  const paperlessInputName = useId();
  const emailInputName = useId();
  const { channel } = useChannelState();

  const blockReload = useRef<boolean>(false);

  const {
    data: getPaperlessSettingsResponse,
    isLoading: isPaperlessSettingsLoading,
    isError: isPaperlessSettingsError,
    error: getPaperlessSettingsError,
    refetch: getPaperlessSettingsRefetch,
  } = useGetPaperlessSettings({
    dwbuid: dwbuid,
  });

  const {
    mutateAsync: updatePaperlessSettings,
    isLoading: isUpdatePaperlessSettingsLoading,
  } = useUpdatePaperlessSettings();

  const [paperlessStatus, setPaperlessStatus] = useState(
    getPaperlessSettingsResponse?.paperlessServiceOptOut ?? false,
  );
  const [emailStatus, setEmailStatus] = useState(
    getPaperlessSettingsResponse?.onlineStatementEmailOptOut ?? false,
  );
  const [stopReEnrollPaperless, setStopReEnrollPaperless] = useState(
    getPaperlessSettingsResponse?.paperlessServiceOptOut ?? false,
  );
  const [updateButtonEnabled, setUpdateButtonEnabled] = useState(false);

  const [scriptDetails, setScriptDetails] =
    useState<PaperlessServiceScriptDetails | null>(null);

  useEffect(() => {
    if (getPaperlessSettingsResponse && !blockReload.current) {
      setPaperlessStatus(getPaperlessSettingsResponse.paperlessServiceOptOut);
      setEmailStatus(getPaperlessSettingsResponse.onlineStatementEmailOptOut);
      setStopReEnrollPaperless(
        getPaperlessSettingsResponse.paperlessServiceOptOut,
      );
    }
  }, [getPaperlessSettingsResponse]);

  const maintainAllDirtyState = (dirty: boolean) => {
    blockReload.current = dirty;
    onSetToolInProgress(toolId, dirty);
    setUpdateButtonEnabled(dirty);
  };

  const onChangePaperlessStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isPaperlessChecked = e.target.checked;
    const dirty =
      isPaperlessChecked !==
      getPaperlessSettingsResponse?.paperlessServiceOptOut;
    maintainAllDirtyState(dirty);
    setPaperlessStatus(isPaperlessChecked);

    // If returning to initial load state, do not show any scripts
    if (!dirty) {
      setScriptDetails(null);
      return;
    }

    if (isPaperlessChecked) {
      setScriptDetails(getPaperlessServiceScriptDetails("SS_0003"));
    } else {
      setScriptDetails(null);
    }
  };

  const onChangeEmailStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isEmailChecked = e.target.checked;
    const dirty =
      // Retain dirty state from paperlessStatus, if applicable
      // Otherwise, set dirty status based on emailStatus
      paperlessStatus !==
        getPaperlessSettingsResponse?.paperlessServiceOptOut ||
      isEmailChecked !==
        getPaperlessSettingsResponse?.onlineStatementEmailOptOut;
    maintainAllDirtyState(dirty);
    setEmailStatus(isEmailChecked);

    // If returning to initial load state, do not show any scripts
    if (!dirty) {
      setScriptDetails(null);
      return;
    }

    if (isEmailChecked) {
      // Only set to SS_0002 if SS_0003 is not currently already being displayed
      if (scriptDetails?.name !== "SS_0003") {
        setScriptDetails(getPaperlessServiceScriptDetails("SS_0002"));
      }
    } else {
      setScriptDetails(
        getPaperlessServiceScriptDetails("SS_0004", {
          initial: getPaperlessSettingsResponse
            ? { ...getPaperlessSettingsResponse }
            : undefined,
          updated: getPaperlessSettingsResponse
            ? {
                ...getPaperlessSettingsResponse,
                onlineStatementEmailOptOut: isEmailChecked,
                // If isEmailChecked is true, paperlessServiceOptOut should always be true
                paperlessServiceOptOut: paperlessStatus || isEmailChecked,
              }
            : undefined,
        }),
      );
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const initialState = getPaperlessSettingsResponse
        ? { ...getPaperlessSettingsResponse }
        : undefined;

      // Set stop re-enrolling for paperless service
      setStopReEnrollPaperless(paperlessStatus || emailStatus);

      await updatePaperlessSettings({
        dwbuid,
        onlineStatementEmailOptOut: emailStatus,
        // If emailStatus is true, paperlessServiceOptOut should always be true
        paperlessServiceOptOut: paperlessStatus || emailStatus,
        partyId: partyId,
        referenceId: referenceId,
        channel: ChannelMapToFull[channel],
      });
      maintainAllDirtyState(false);
      // Get script details for after submit
      setScriptDetails(
        getPaperlessServiceScriptDetails("SS_0005", {
          initial: initialState,
          updated: initialState
            ? {
                ...initialState,
                onlineStatementEmailOptOut: emailStatus,
                // If emailStatus is true, paperlessServiceOptOut should always be true
                paperlessServiceOptOut: paperlessStatus || emailStatus,
              }
            : undefined,
        }),
      );
    } catch (err) {
      onDisplayGeneralErrors(err);

      // Reset stop re-enroll state on failure
      setStopReEnrollPaperless(
        getPaperlessSettingsResponse?.paperlessServiceOptOut ?? false,
      );
    }
  };

  return (
    <form
      className={classNames(toolStyles.container, className)}
      id={toolId}
      onSubmit={onSubmit}
    >
      <Container>
        <Row
          className={classNames(
            toolStyles.header,
            toolStyles.headerWithDivider,
          )}
        >
          <Col className={toolStyles.centerCol} md={9}>
            <h2 className={toolStyles.title}>
              {toolTypeToName(ToolType.PaperlessService)}
            </h2>
          </Col>
          <Col className={toolStyles.centerCol} md={1.5}>
            <StyledButton
              type="submit"
              disabled={
                isPaperlessSettingsLoading ||
                isPaperlessSettingsError ||
                isDisabled ||
                (!getPaperlessSettingsResponse?.email?.primaryEmail &&
                  !emailStatus) ||
                !updateButtonEnabled
              }
              isLoading={isUpdatePaperlessSettingsLoading}
              className={toolStyles.headerButton}
            >
              {strings.update}
            </StyledButton>
          </Col>
          <Col className={toolStyles.centerCol} md={1.5}>
            <StyledButton
              className={toolStyles.headerButton}
              variant="secondary"
              onClick={onRequestClose}
            >
              {strings.close}
            </StyledButton>
          </Col>
        </Row>
        {isPaperlessSettingsLoading ? (
          <Row>
            <Col>
              <Spinner />
            </Col>
          </Row>
        ) : isPaperlessSettingsError ? (
          <Row>
            <Col>
              <ToolLoadError
                toolName={toolTypeToName(ToolType.PaperlessService)}
                errorDescription={
                  getDisplayError(getPaperlessSettingsError)?.message
                }
                onRetry={getPaperlessSettingsRefetch}
              />
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={4}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  {strings.paperlessStatusTitle}
                </h3>
                <StyledCheckbox
                  name={paperlessInputName}
                  disabled={
                    isUpdatePaperlessSettingsLoading ||
                    emailStatus ||
                    getPaperlessSettingsResponse.email.emailOptOut ||
                    stopReEnrollPaperless ||
                    isDelinquent ||
                    isDisabled
                  }
                  className={styles.input}
                  iconClassName={styles.inputIcon}
                  labelClassName={styles.inputLabel}
                  label={strings.paperlessStatusLabel}
                  checked={
                    paperlessStatus ||
                    emailStatus ||
                    getPaperlessSettingsResponse.email.emailOptOut ||
                    isDelinquent
                  }
                  onChange={onChangePaperlessStatus}
                />
              </div>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  {strings.emailStatusTitle}
                </h3>
                <StyledCheckbox
                  name={emailInputName}
                  disabled={
                    isUpdatePaperlessSettingsLoading ||
                    getPaperlessSettingsResponse.email.emailOptOut ||
                    isDisabled
                  }
                  className={styles.input}
                  iconClassName={styles.inputIcon}
                  labelClassName={styles.inputLabel}
                  label={strings.emailStatusLabel}
                  checked={
                    emailStatus ||
                    getPaperlessSettingsResponse.email.emailOptOut
                  }
                  onChange={onChangeEmailStatus}
                />
                {!getPaperlessSettingsResponse.email.primaryEmail &&
                  !emailStatus && (
                    <div className={styles.validationMessages}>
                      {strings.a0162}
                    </div>
                  )}
              </div>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  {strings.emailContactTitle}
                </h3>
                <div className={styles.emailContent}>
                  <div>{strings.emailContactDescription}</div>
                  {getPaperlessSettingsResponse.email.emailOptOut ? (
                    <div className={styles.warningText}>
                      {strings.emailContactOptedOut}
                    </div>
                  ) : (
                    <>
                      {getPaperlessSettingsResponse.email.primaryEmail && (
                        <div>
                          <div className={styles.emailLabel}>
                            {strings.primaryEmail}
                          </div>
                          <div className={styles.emailValue}>
                            {getPaperlessSettingsResponse.email.primaryEmail}
                          </div>
                        </div>
                      )}
                      {getPaperlessSettingsResponse.email.alternateEmail && (
                        <div>
                          <div className={styles.emailLabel}>
                            {strings.alternateEmail}
                          </div>
                          <div className={styles.emailValue}>
                            {getPaperlessSettingsResponse.email.alternateEmail}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Col>
            <Col md={8}>
              {scriptDetails ? (
                <ConversationScript
                  scripts={scriptDetails.scripts}
                  scriptName={scriptDetails.name}
                />
              ) : undefined}
            </Col>
          </Row>
        )}
      </Container>
    </form>
  );
}
